/* eslint-disable no-param-reassign */
import produce, {castDraft} from 'immer';

import {PredicateOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DataFieldsTableModel} from '../../../../models/api/dataFieldsTableModel';
import {EditorErrorsEnum} from '../../../editors/commons/editorErrors';
import {ConditionsActionTypesEnum} from '../../../editors/conditions/conditionsActions';
import {isExpressionAction} from '../../../editors/expressions/expressionActions';
import {expressionReducer} from '../../../editors/expressions/expressionReducer';
import {ClientRuleAction} from '../../ruleActionModel';
import {ClientRuleCondition} from '../../ruleConditionModel';
import {RuleIntegration} from '../../ruleIntegrationModel';
import {TriggerWithConditions, triggerWithInitialConditions} from '../../ruleTriggerHelpers';
import {RuleTriggersTypesEnum} from '../../ruleTriggerModel';
import {RuleEditorAction, RuleEditorActionTypesEnum} from './ruleEditorActions';
import {EditorStateErrorMap, RuleEditorState} from './ruleEditorState';

/*
 * Reducer.
 */

export function ruleEditorReducer(state: RuleEditorState, action: RuleEditorAction): RuleEditorState {
  if (isExpressionAction(action)) {
    return expressionReducer(state, action, {dataTable: state.dataTable});
  }

  switch (action.type) {
    case RuleEditorActionTypesEnum.UPDATE_RULE_NAME:
      return updateRuleNameReducer(state, action.name);

    case RuleEditorActionTypesEnum.UPDATE_EXAMPLE_RESOURCE_ID:
      return UpdateExampleResourceId(state, action.resourceId);

    case RuleEditorActionTypesEnum.TOGGLE_IS_ACTIVE:
      return toggleRuleIsActiveReducer(state);

    case RuleEditorActionTypesEnum.TOGGLE_STOP_PROCESSING:
      return toggleStopProcessingReducer(state);

    case RuleEditorActionTypesEnum.TOGGLE_ERROR:
      return toggleErrorReducer(state, action.key, action.error);

    case RuleEditorActionTypesEnum.SET_HIDDEN_ERRORS:
      return setHiddenErrorsReducer(state, action.cards);

    case RuleEditorActionTypesEnum.ADD_TRIGGER:
      return addTriggerReducer(state, action.trigger, action.ruleIntegration);

    case RuleEditorActionTypesEnum.UPDATE_TRIGGER:
      return updateTriggerReducer(state, action.triggerWithConditions);

    case RuleEditorActionTypesEnum.DELETE_TRIGGER:
      return deleteTriggerReducer(state, action.trigger);

    case ConditionsActionTypesEnum.ADD_CONDITION:
      return addConditionReducer(state, action.indexAND, action.condition);

    case ConditionsActionTypesEnum.UPDATE_CONDITION:
      return updateConditionReducer(state, action.indexAND, action.predicate, action.update);

    case ConditionsActionTypesEnum.DELETE_CONDITION:
      return deleteConditionReducer(state, action.indexAND, action.predicate);

    case RuleEditorActionTypesEnum.ADD_ACTION:
      return addActionReducer(state, action.condition);

    case RuleEditorActionTypesEnum.UPDATE_ACTION:
      return updateActionReducer(state, action.predicate, action.update);

    case RuleEditorActionTypesEnum.DELETE_ACTION:
      return deleteActionReducer(state, action.predicate);

    case RuleEditorActionTypesEnum.UPDATE_TABLE:
      return setTable(state, state.dataTable && action.updater(state.dataTable));

    case RuleEditorActionTypesEnum.SET_TABLE:
      return setTable(state, action.table);

    default:
      return state;
  }
}

/*
 * Helpers.
 */

function updateRuleNameReducer(state: RuleEditorState, name: string) {
  return produce(state, (draft) => {
    draft.ruleState.name = name;
  });
}

function UpdateExampleResourceId(state: RuleEditorState, exampleResourceId: string | undefined) {
  return produce(state, (draft) => {
    draft.ruleState.exampleResourceId = exampleResourceId;
    draft.hasDryRunTestData = Boolean(exampleResourceId);
  });
}

function toggleRuleIsActiveReducer(state: RuleEditorState) {
  return produce(state, (draft) => {
    draft.ruleState.isActive = !draft.ruleState.isActive;
  });
}

function toggleStopProcessingReducer(state: RuleEditorState) {
  return produce(state, (draft) => {
    draft.ruleState.shouldStopProcessing = !draft.ruleState.shouldStopProcessing;
  });
}

function toggleErrorReducer(state: RuleEditorState, key: string, error?: EditorErrorsEnum) {
  return produce(state, (draft) => {
    if (error) {
      draft.errors[key] = error;
    } else {
      delete draft.errors[key];
    }
  });
}

function setHiddenErrorsReducer(state: RuleEditorState, cards: ReadonlyArray<string> = []) {
  return produce(state, (draft) => {
    draft.notDisplayingErrorCards = castDraft(cards);
  });
}

function addTriggerReducer(
  state: RuleEditorState,
  trigger: RuleTriggersTypesEnum,
  ruleIntegration: RuleIntegration,
) {
  return produce(state, (draft) => {
    const triggerWithConditions = triggerWithInitialConditions(trigger, ruleIntegration);
    draft.notDisplayingErrorCards = [triggerWithConditions.trigger];
    draft.ruleState.triggers.push(castDraft(triggerWithConditions));
  });
}

function updateTriggerReducer(state: RuleEditorState, triggerWithConditions: TriggerWithConditions) {
  return produce(state, (draft) => {
    const {triggers} = draft.ruleState;
    const index = triggers.findIndex((t) => t.trigger === triggerWithConditions.trigger);

    if (index >= 0) {
      triggers[index] = castDraft(triggerWithConditions);
    }

    draft.notDisplayingErrorCards = [];
  });
}

function deleteTriggerReducer(state: RuleEditorState, trigger: RuleTriggersTypesEnum) {
  return produce(state, (draft) => {
    const index = draft.ruleState.triggers.findIndex((t) => t.trigger === trigger);

    if (index >= 0) {
      if (draft.errors[trigger]) {
        delete draft.errors[trigger];
      }
    }

    draft.ruleState.triggers.splice(index, 1);
    draft.notDisplayingErrorCards = [];
  });
}

function addConditionReducer(state: RuleEditorState, indexAND: number, condition: ClientRuleCondition) {
  return produce(state, (draft) => {
    const newCondition = castDraft(condition);
    draft.notDisplayingErrorCards = [newCondition.clientId];

    if (!draft.ruleState.conditions[indexAND]) {
      // Append an 'AND' condition to the end.
      draft.ruleState.conditions.push([newCondition]);
      return;
    }

    // Append an 'OR' condition to an existing 'AND' node.
    draft.ruleState.conditions[indexAND].push(newCondition);
  });
}

function updateConditionReducer(
  state: RuleEditorState,
  indexAND: number,
  predicate: PredicateOf<ClientRuleCondition>,
  update: (cond: ClientRuleCondition) => ClientRuleCondition,
) {
  return produce(state, (draft) => {
    draft.notDisplayingErrorCards = [];

    const orConditions = draft.ruleState.conditions[indexAND];
    if (!orConditions) {
      return;
    }

    for (let i = 0; i < orConditions.length; i++) {
      if (predicate(orConditions[i])) {
        orConditions[i] = castDraft(update(orConditions[i]));
        break;
      }
    }
  });
}

function deleteConditionReducer(
  state: RuleEditorState,
  indexAND: number,
  predicate: PredicateOf<ClientRuleCondition>,
) {
  return produce(state, (draft) => {
    draft.notDisplayingErrorCards = [];

    const orConditions = draft.ruleState.conditions[indexAND];
    if (!orConditions) {
      return;
    }

    // Delete condition in 'OR' list.
    const index = orConditions.findIndex(predicate);

    const condition = orConditions[index];
    if (condition && draft.errors[condition.clientId]) {
      delete draft.errors[condition.clientId];
    }

    if (index >= 0) {
      orConditions.splice(index, 1);
    }

    // If 'OR' list is empty, delete 'OR' item in 'AND' list.
    if (orConditions.length === 0) {
      draft.ruleState.conditions.splice(indexAND, 1);
    }
  });
}

export function addActionReducerHelper(actions: ReadonlyArray<ClientRuleAction>, action: ClientRuleAction) {
  return [...actions, action];
}

function addActionReducer(state: RuleEditorState, action: ClientRuleAction) {
  const newActions = addActionReducerHelper(state.ruleState.actions, action);

  return {
    ...state,
    ruleState: {...state.ruleState, actions: newActions},
    notDisplayingErrorCards: [newActions[newActions.length - 1].clientId],
  };
}

export function updateActionReducerHelper(
  actions: ReadonlyArray<ClientRuleAction>,
  predicate: PredicateOf<ClientRuleAction>,
  update: (action: ClientRuleAction) => ClientRuleAction,
) {
  return produce(actions, (actionsDraft) => {
    for (let i = 0; i < actionsDraft.length; i++) {
      if (predicate(actionsDraft[i])) {
        actionsDraft[i] = castDraft(update(actionsDraft[i]));
        break;
      }
    }
  });
}

export function updateActionReducer(
  state: RuleEditorState,
  predicate: PredicateOf<ClientRuleAction>,
  update: (action: ClientRuleAction) => ClientRuleAction,
) {
  const newActions = updateActionReducerHelper(state.ruleState.actions, predicate, update);

  return {
    ...state,
    ruleState: {
      ...state.ruleState,
      actions: newActions,
    },
    notDisplayingErrorCards: [],
  };
}

export function deleteActionReducerHelper<TErrors extends EditorStateErrorMap | undefined>(
  actions: ReadonlyArray<ClientRuleAction>,
  predicate: PredicateOf<ClientRuleAction>,
  errors: TErrors,
) {
  return produce({actions, errors}, (draft) => {
    const index = draft.actions.findIndex(predicate);

    const stateAction = draft.actions[index];
    if (stateAction && draft.errors && draft.errors[stateAction.clientId]) {
      delete draft.errors[stateAction.clientId];
    }

    if (index >= 0) {
      draft.actions.splice(index, 1);
    }
  });
}

export function deleteActionReducer(state: RuleEditorState, predicate: PredicateOf<ClientRuleAction>) {
  const {actions, errors} = deleteActionReducerHelper(state.ruleState.actions, predicate, state.errors);

  return {
    ...state,
    errors,
    ruleState: {
      ...state.ruleState,
      actions,
    },
    notDisplayingErrorCards: [],
  };
}

export function setTable(
  state: RuleEditorState,
  dataTable: DataFieldsTableModel | undefined,
): RuleEditorState {
  return {
    ...state,
    dataTable,
  };
}
